import {
  createBaseThemeOptions,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

/**
 * Fressnapf | maxi zoo brand palette.
 *
 * `green`, `greenBright`, `cream`, `red` and `grey` are the corporate values.
 * The `ink*` surfaces have no brand equivalent - the corporate design is
 * light-only - so they are derived here to give the dark theme a warm, slightly
 * green-shifted neutral instead of a flat grey.
 */
const fn = {
  green: '#086521',
  greenBright: '#00a332',
  greenSoft: '#0b7f2a',
  greenOnDark: '#7de29a',
  cream: '#fff4e8',
  white: '#ffffff',
  red: '#ff372e',
  amber: '#e8a33d',
  grey: '#5f6765',
  ink: '#101a13',
  inkRaised: '#18231b',
  // Green-tinted so the sidebar separates from the neutral page background
  // instead of merging into it.
  inkNav: '#0d2314',
} as const;

const bodyFont = `'FNMZ Skolar Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`;

/**
 * BoldenVan is the FN display face. It is deliberately scoped to the two
 * largest heading levels - set this to `bodyFont` to run Skolar Sans throughout.
 */
const displayFont = `'FNMZ BoldenVan', ${bodyFont}`;

const fnTypography = {
  ...defaultTypography,
  fontFamily: bodyFont,
  h1: { ...defaultTypography.h1, fontFamily: displayFont },
  h2: { ...defaultTypography.h2, fontFamily: displayFont },
};

/** Header banner gradients. Every Backstage page theme gets the FN green ramp. */
const genFnPageThemes = (colors: [string, string]) => ({
  home: genPageTheme({ colors, shape: shapes.wave }),
  documentation: genPageTheme({ colors, shape: shapes.wave2 }),
  tool: genPageTheme({ colors, shape: shapes.round }),
  service: genPageTheme({ colors, shape: shapes.wave }),
  website: genPageTheme({ colors, shape: shapes.wave }),
  library: genPageTheme({ colors, shape: shapes.wave }),
  other: genPageTheme({ colors, shape: shapes.wave }),
  app: genPageTheme({ colors, shape: shapes.wave }),
  apis: genPageTheme({ colors, shape: shapes.wave }),
});

export const fnLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: { main: fn.green, dark: '#054d18', light: fn.greenSoft },
      secondary: { main: fn.greenBright, dark: fn.greenSoft, light: '#4fd06f' },
      error: { main: fn.red },
      warning: { main: fn.amber },
      info: { main: fn.greenSoft },
      success: { main: fn.greenBright },
      background: { default: fn.cream, paper: fn.white },
      text: { primary: '#1a1f1c', secondary: fn.grey },
      banner: {
        info: fn.green,
        error: fn.red,
        text: fn.white,
        link: fn.cream,
      },
      errorBackground: '#ffe5e3',
      warningBackground: '#fdf1de',
      infoBackground: '#e7f3ea',
      navigation: {
        background: fn.green,
        indicator: fn.greenBright,
        color: '#d9e8dd',
        selectedColor: fn.white,
        navItem: { hoverBackground: '#0a7a29' },
      },
    },
  }),
  fontFamily: bodyFont,
  typography: fnTypography,
  defaultPageTheme: 'home',
  pageTheme: genFnPageThemes([fn.green, fn.greenBright]),
});

export const fnDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: { main: fn.greenOnDark, dark: fn.greenBright, light: '#a8edbc' },
      secondary: {
        main: fn.greenBright,
        dark: fn.greenSoft,
        light: fn.greenOnDark,
      },
      error: { main: '#ff6b64' },
      warning: { main: fn.amber },
      info: { main: fn.greenOnDark },
      success: { main: fn.greenBright },
      background: { default: fn.ink, paper: fn.inkRaised },
      text: { primary: '#e8efe9', secondary: '#9aa8a0' },
      banner: {
        info: fn.greenSoft,
        error: '#8c221d',
        text: fn.white,
        link: fn.greenOnDark,
      },
      errorBackground: '#3a1613',
      warningBackground: '#3a2c12',
      infoBackground: '#12301c',
      navigation: {
        background: fn.inkNav,
        indicator: fn.greenBright,
        color: '#9aa8a0',
        selectedColor: fn.white,
        navItem: { hoverBackground: '#17351f' },
      },
    },
  }),
  fontFamily: bodyFont,
  typography: fnTypography,
  defaultPageTheme: 'home',
  pageTheme: genFnPageThemes(['#043d14', fn.greenSoft]),
});
